<template>
  <div class="radio-input"
       :class="brandClass">
    <b-form-radio :id="`${id}RadioButton`"
                  :aria-invalid="showError"
                  :disabled="disabled"
                  :name="radioGroupName"
                  :state="showError"
                  :value="radioValue"
                  v-model="modelValue">
      <slot></slot>
    </b-form-radio>
  </div>
</template>

<script>
import isEmpty from '../../../utility/isEmpty'
import Logger from '../../../utility/loggerUtil'
import Router from '../../../utility/routerUtil'

export default {
  name: 'radio-input',

  props: {
    id: {
      type: [String],
      default: 'radioButton'
    },

    errorIds: {
      type: [String],
      default: ''
    },

    radioGroupName: {
      type: [String],
      required: true
    },

    hasError: {
      type: [Boolean],
      default: false
    },

    disabled: {
      type: [Boolean],
      default: false
    },

    radioValue: {
      default: null
    },

    value: {
      default: null
    }
  },

  data () {
    return {
      brandClass: Router.getCurrentRouteBrandClass(this.$route)
    }
  },

  mounted () {
    const byId = `${this.id}RadioButton`
    try {
      if (!isEmpty(this.errorIds)) {
        document.getElementById(byId).setAttribute('aria-describedby', this.errorIds)
      }
    } catch (e) {
      Logger.debug(`Error setting attribute on radio button ${byId}: ${e}`)
    }
  },

  computed: {
    showError () {
      return this.hasError === true ? false : null
    },

    modelValue: {
      get () {
        return this.value
      },

      set (value) {
        this.$emit('input', this.radioValue)
      }
    }
  }
}
</script>

<style scoped lang="scss">
@use '../../../assets/styles/colors';

.radio-input {
  display: inline-flex;
  margin-right: 1rem;
}

::v-deep input {
  top: 0.25rem;
  width: 1.625rem;
  height: 1.625rem;
}

::v-deep label.custom-control-label {
  padding-top: 0.35rem;
  padding-left: 0.75rem;
}

::v-deep .custom-radio .custom-control-label::before {
  width: 1.625rem;
  height: 1.625rem;
  cursor: pointer;
  background: transparent;
  border: 1px solid #c4c4c4;
  border-radius: 50%;
}

::v-deep .custom-control-input.is-invalid ~ .custom-control-label::before {
  border-color: colors.$red-1;
}

::v-deep .custom-control-input:disabled ~ .custom-control-label::before {
  background-color: colors.$gray-200;
}

::v-deep .custom-control-input:checked ~ .custom-control-label::before {
  width: 1.625rem;
  height: 1.625rem;
  cursor: pointer;
  background: transparent;
  border: 1px solid #c4c4c4;
  border-radius: 50%;
}

::v-deep .custom-control-input:checked.is-invalid ~ .custom-control-label::before {
  background-color: transparent;
  border-color: colors.$red-1;
}

.c1l ::v-deep .custom-control-input:focus ~ .custom-control-label::before {
  outline: colors.$c1l--orange-1 solid 3px;
  outline-offset: 3px;
  box-shadow: none;
}

.ccc ::v-deep .custom-control-input:focus ~ .custom-control-label::before {
  outline: colors.$ccc--purple-1 solid 3px;
  outline-offset: 3px;
  box-shadow: none;
}

.ccf ::v-deep .custom-control-input:focus ~ .custom-control-label::before {
  outline: colors.$ccf--orange-1 solid 3px;
  outline-offset: 3px;
  box-shadow: none;
}

.chs ::v-deep .custom-control-input:focus ~ .custom-control-label::before {
  outline: colors.$chs--green-3 solid 3px;
  outline-offset: 3px;
  box-shadow: none;
}

.cic ::v-deep .custom-control-input:focus ~ .custom-control-label::before {
  outline: colors.$cic--green-2 solid 3px;
  outline-offset: 3px;
  box-shadow: none;
}

.esm ::v-deep .custom-control-input:focus ~ .custom-control-label::before {
  outline: colors.$esm--orange-1 solid 3px;
  outline-offset: 3px;
  box-shadow: none;
}

.inl ::v-deep .custom-control-input:focus ~ .custom-control-label::before {
  outline: colors.$inl--yellow-1 solid 3px;
  outline-offset: 3px;
  box-shadow: none;
}

.tmx ::v-deep .custom-control-input:focus ~ .custom-control-label::before {
  outline: rgba(0, 125, 250, 0.6) solid 3px;
  outline-offset: 3px;
  box-shadow: none;
}

::v-deep .custom-control-input:focus:not(:checked) ~ .custom-control-label::before {
  border-color: #c4c4c4;
}

::v-deep .custom-control-input.is-invalid:focus:not(:checked) ~ .custom-control-label::before {
  border-color: colors.$red-1;
}

::v-deep .custom-control-input:checked ~ .custom-control-label::after {
  z-index: -9999;
  width: 1.525rem;
  height: 1.525rem;
  margin-top: 0.06rem;
  margin-left: 0.06rem;
  cursor: pointer;
  border-width: 2px;
}

.c1l ::v-deep .custom-control-input:checked ~ .custom-control-label::after {
  background: radial-gradient(colors.$c1l--green-1 55%, colors.$white 60%);
}

.ccc ::v-deep .custom-control-input:checked ~ .custom-control-label::after {
  background: radial-gradient(colors.$ccc--blue-2 55%, colors.$white 60%);
}

.ccf ::v-deep .custom-control-input:checked ~ .custom-control-label::after {
  background: radial-gradient(colors.$ccf--navy-4 55%, colors.$white 60%);
}

.chs ::v-deep .custom-control-input:checked ~ .custom-control-label::after {
  background: radial-gradient(colors.$chs--green-1 55%, colors.$white 60%);
}

.cic ::v-deep .custom-control-input:checked ~ .custom-control-label::after {
  background: radial-gradient(colors.$cic--green-1 55%, colors.$white 60%);
}

.esm ::v-deep .custom-control-input:checked ~ .custom-control-label::after {
  background: radial-gradient(colors.$esm--orange-2 55%, colors.$white 60%);
}

.inl ::v-deep .custom-control-input:checked ~ .custom-control-label::after {
  background: radial-gradient(colors.$inl--green-1 55%, colors.$white 60%);
}

.tmx ::v-deep .custom-control-input:checked ~ .custom-control-label::after {
  background: radial-gradient(colors.$tmx--blue-1 55%, colors.$white 60%);
}

::v-deep .custom-control-input:disabled:checked ~ .custom-control-label::after {
  background: radial-gradient(colors.$gray-300 55%, colors.$gray-200 60%);
  border-radius: 50%;
}

::v-deep .custom-control-input.is-invalid:checked ~ .custom-control-label::after {
  background: radial-gradient(colors.$red-1 55%, colors.$white 60%);
}

::v-deep .custom-radio .custom-control-input:disabled:checked ~ .custom-control-label::before {
  background-color: transparent;
}
</style>
